import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AtsUtility } from '@ats/utility';
import { LocalizationService } from '@cm/core/localization';
import { CmEventService } from '@cm/core/system';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { CmInterceptHeaders } from '../models';

const IGNORED_ERRORS_BY_ROUTE = {
	'public/first-time-login': ['Validation.DataNotFound'],
};

@Injectable({
	providedIn: 'root',
})
export class CmLoggingInterceptor implements HttpInterceptor {
	// Changes to constructor params necessitate updates to deps in core.module
	// libs\cm\core\src\lib\core.module.ts
	constructor(
		private eventService: CmEventService,
		private locService: LocalizationService,
		private router: Router
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const ignoreAuthError = req.headers.has(CmInterceptHeaders.Type.IgnoreAuthError);

		const suppressError = req.headers.has(CmInterceptHeaders.Type.SuppressError);
		if (suppressError) {
			req = req.clone({
				headers: req.headers.delete(CmInterceptHeaders.Type.SuppressError),
			});
		}

		const hasSuppressHeader = req.headers.has(CmInterceptHeaders.Type.SuppressErrorLog);
		if (hasSuppressHeader) {
			req = req.clone({
				headers: req.headers.delete(CmInterceptHeaders.Type.SuppressErrorLog),
			});
		}

		const suppressErrorId = req.headers.get(CmInterceptHeaders.Type.SuppressErrorById);
		if (suppressErrorId) {
			req = req.clone({
				headers: req.headers.delete(CmInterceptHeaders.Type.SuppressErrorById),
			});
		}

		return next.handle(req).pipe(
			catchError(err => {
				const error = (err || {}).error ? err.error : err;

				const errorLoc$ = error?.id
					? this.locService
							.find$(error.id)
							.pipe(
								map(loc =>
									!!loc ? loc.value : this.locService.findGUIDLocName(error.id)
								)
							)
					: of(null);

				return errorLoc$.pipe(
					take(1),
					switchMap(errorLoc => {
						if (!suppressError) {
							if (this.isForbidden(err)) {
								// Handle auth errors as long as they aren't ignored
								if (!error.id && !ignoreAuthError) {
									this.eventService.handleAuthError();
								}
							} else if (this.is500(err)) {
								// Handle 500 errors
								this.eventService.handleSystemError();
							} else if (this.getErrorCode(err) !== 0) {
								this.eventService.handleAPIError({
									guid: error.id,
									message: error.message,
								});
							}
						}
						/**
						 * Do not log under these conditions:
						 * 1. HasSuppressHeader - We explicitly told it not to
						 * 2. Ignore 401/403 errors
						 * 3. If we have a GUID mapped we expected it
						 * 4. Some errors we don't care about, like 409 or 500+
						 * 5. The error is explicitly ignored above
						 */
						const doNotLog =
							hasSuppressHeader ||
							this.isForbidden(err) ||
							!!errorLoc ||
							this.isIgnoredStatus(err) ||
							this.isIgnoredByRoute(error) ||
							this.hasSuppressErrorId(error, suppressErrorId);
						return throwError(() => ({ ...error, raw: err, doNotLog, req }));
					})
				);
			})
		);
	}

	private hasSuppressErrorId(err: any, suppressErrorId: string) {
		return suppressErrorId ? err?.id === suppressErrorId : false;
	}

	private is500(err: any) {
		const code = this.getErrorCode(err);
		return code === 500;
	}

	private isIgnoredStatus(err: any) {
		const code = this.getErrorCode(err);
		return code === 409 || code > 499 || code === 0;
	}

	private isForbidden(err: any) {
		const code = this.getErrorCode(err);
		return code === 401 || code === 403;
	}

	private isIgnoredByRoute(err: any) {
		const id = (err || {}).id ? err.id : ((err || {}).error || {}).id;
		return Object.keys(IGNORED_ERRORS_BY_ROUTE)
			.filter(key => this.router.routerState.snapshot.url.includes(key))
			.some(key => IGNORED_ERRORS_BY_ROUTE[key].includes(id));
	}

	private getErrorCode(err: any) {
		const codeKey = AtsUtility.isTruthyOrZero(err.status) ? 'status' : 'statusCode';
		return err[codeKey];
	}
}
